import {
  Home,
  LogOut,
  UserCircle,
  Moon,
  Sun,
  Bell,
  Globe,
  ChevronDown,
  Settings,
} from "lucide-react";
import { languageNamesLabels } from "@/i18n";
import type { Language } from "@/utils/languageUtils";
import { Button } from "../ui/button";
import { useNavigate, useLocation } from "react-router-dom";

import { shouldHideBottomNav, shouldShowGuestBottomNav } from "@/lib/native/nativeRoutes";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useHasUnreadNotifications } from "@/hooks/useHasUnreadNotifications";
import { toast } from "sonner";
import { commonMenuLabels, commonAuthLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { NativeOverlaySheet } from "@/components/native/NativeOverlaySheet";
import { NativeOptionSheet, type NativeOption } from "@/components/native/NativeOptionSheet";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";

type UserActionsProps = {
  /** Native logged-in: floating profile chip (no header bar). */
  floating?: boolean;
  /** Skip menu open after dragging the floating bubble. */
  suppressClick?: React.MutableRefObject<boolean>;
};

const UserActions = ({ floating = false, suppressClick }: UserActionsProps) => {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const { user, logoutAsync } = useAuth();
  const { isNative: isNativeApp } = useNativePlatform();
  const path = location.pathname;
  const guestBottomNavVisible =
    isNativeApp && !user && shouldShowGuestBottomNav(path);
  const hideHomeButton =
    floating ||
    (isNativeApp &&
      Boolean(user) &&
      !shouldHideBottomNav(path) &&
      !path.startsWith("/auctions"));
  const isMobile = useIsMobile();
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const [isLangOpen, setIsLangOpen] = useState(false);

  const { hasUnreadNotifications } = useHasUnreadNotifications();
  const hasUnreadItems = hasUnreadNotifications;

  const menuLabels = commonMenuLabels(language);
  const authLabels = commonAuthLabels(language);
  const langNames = languageNamesLabels(language);

  const handleLogin = () => {
    navigate("/auth");
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logoutAsync();
      setIsOpen(false);
      toast.success(authLabels.logoutSuccess, { duration: 3000 });
      navigate(isNativeApp ? "/auctions" : "/");
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string })?.message || "";
      toast.error(authLabels.logoutError.replace("{error}", errorMessage), {
        duration: 5000,
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const closeMenuThen = (action: () => void) => {
    setIsOpen(false);
    if (isNativeApp) {
      window.setTimeout(action, 50);
    } else {
      action();
    }
  };

  const handleProfile = () => {
    closeMenuThen(() => navigate("/settings?tab=profile"));
  };

  const handleSettings = () => {
    closeMenuThen(() => navigate("/settings"));
  };

  const handleNotifications = () => {
    closeMenuThen(() => navigate("/notifications"));
  };

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  const handleClick = () => {
    if (isNativeApp || isMobile) {
      setIsOpen(!isOpen);
    }
  };

  const languageOptions: NativeOption[] = (["sv", "en"] as const).map(
    (lang) => ({
      value: lang,
      label: langNames[lang],
    }),
  );

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [user]);

  useEffect(() => {
    if (!isOpen) setIsLangOpen(false);
  }, [isOpen]);

  // Web/mobile dropdown only — native uses bottom sheet (capture listener would close before menu click)
  useEffect(() => {
    if (!isMobile || !isOpen || isNativeApp) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedButton = dropdownRef.current?.contains(target);
      const clickedDropdown = dropdownContentRef.current?.contains(target);

      if (!clickedButton && !clickedDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isMobile, isOpen, isNativeApp]);

  const DropdownContent = () => (
    <div
      ref={dropdownContentRef}
      className="fixed w-48 bg-white dark:bg-zinc-950 border border-gray-200 dark:border-white/15 rounded-md shadow-xl z-[100]"
      style={{
        top: dropdownPosition.top,
        right: dropdownPosition.right,
      }}
      onMouseEnter={!isMobile ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="py-1">
        <button
          onClick={handleProfile}
          className="w-full px-4 py-2.5 md:py-2 text-left text-base md:text-sm hover:bg-gray-100 dark:hover:bg-white/10 flex items-center"
        >
          <UserCircle className="h-3.5 w-3.5 md:h-3 md:w-3 mr-2" />
          {menuLabels.profile}
        </button>
        <button
          onClick={handleSettings}
          className="w-full px-4 py-2.5 md:py-2 text-left text-base md:text-sm hover:bg-gray-100 dark:hover:bg-white/10 flex items-center"
        >
          <Settings className="h-3.5 w-3.5 md:h-3 md:w-3 mr-2" />
          {menuLabels.settings}
        </button>
        <button
          onClick={handleNotifications}
          className="w-full px-4 py-2.5 md:py-2 text-left text-base md:text-sm hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-between"
        >
          <div className="flex items-center">
            <Bell className="h-3.5 w-3.5 md:h-3 md:w-3 mr-2" />
            {menuLabels.notifications}
          </div>
          {hasUnreadNotifications && (
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          )}
        </button>
        <div className="border-t border-gray-200 dark:border-white/10 my-1"></div>
        <div className="pl-4 pr-1 py-2 flex items-center justify-between">
          <div className="flex items-center">
            {isDark ? (
              <Moon className="h-3.5 w-3.5 md:h-3 md:w-3 mr-2" />
            ) : (
              <Sun className="h-3.5 w-3.5 md:h-3 md:w-3 mr-2" />
            )}
            <span className="text-base md:text-sm">
              {isDark ? menuLabels.darkModeActive : menuLabels.lightModeActive}
            </span>
          </div>
          <Switch
            checked={isDark}
            onCheckedChange={toggleTheme}
            className="shrink-0"
          />
        </div>
        <div className="relative pl-4 pr-1 py-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsLangOpen((v) => !v);
            }}
            className="w-full flex items-center gap-2 text-base md:text-sm hover:bg-gray-100 dark:hover:bg-white/10 rounded py-1.5 pr-2 text-left"
          >
            <Globe className="h-3 w-3 shrink-0" />
            <span className="flex-1">{langNames[language]}</span>
            <ChevronDown
              className={`h-3 w-3 shrink-0 transition-transform ${isLangOpen ? "rotate-180" : ""}`}
            />
          </button>
          {isLangOpen && (
            <div
              className="absolute left-0 right-0 top-full mt-0.5 py-1 bg-white dark:bg-black border border-gray-200 dark:border-white/10 rounded-md shadow-lg z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {(["sv", "en"] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => {
                    setLanguage(lang as Language);
                    setIsLangOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-base md:text-sm hover:bg-gray-100 dark:hover:bg-white/10 flex items-center ${
                    language === lang ? "text-accent font-medium" : ""
                  }`}
                >
                  {langNames[lang]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-gray-200 dark:border-white/10" />
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className="w-full px-4 py-2.5 text-left text-base md:text-sm font-medium text-red-600 dark:text-red-400 hover:bg-muted flex items-center rounded-b-md"
      >
        {isLoggingOut ? (
          <span className="h-3 w-3 mr-2 animate-spin border-2 border-t-transparent border-gray-400 rounded-full" />
        ) : (
          <LogOut className="h-3 w-3 mr-2" />
        )}
        {menuLabels.logout}
      </button>
    </div>
  );

  if (!user) {
    return null;
  }

  const nativeUserMenu = (
    <>
      <NativeOverlaySheet
        open={isOpen}
        onOpenChange={setIsOpen}
        title={menuLabels.profile}
      >
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={handleProfile}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left text-base hover:bg-muted active:bg-muted"
          >
            <UserCircle className="h-5 w-5 shrink-0" />
            {menuLabels.profile}
          </button>
          <button
            type="button"
            onClick={handleSettings}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left text-base hover:bg-muted active:bg-muted"
          >
            <Settings className="h-5 w-5 shrink-0" />
            {menuLabels.settings}
          </button>
          <button
            type="button"
            onClick={handleNotifications}
            className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3.5 text-left text-base hover:bg-muted active:bg-muted"
          >
            <span className="flex items-center gap-3">
              <Bell className="h-5 w-5 shrink-0" />
              {menuLabels.notifications}
            </span>
            {hasUnreadNotifications && (
              <span className="h-2 w-2 rounded-full bg-red-500" />
            )}
          </button>
          <div className="my-2 border-t border-border" />
          <div className="flex items-center justify-between gap-3 rounded-lg px-4 py-3.5">
            <span className="flex items-center gap-3 text-base">
              {isDark ? <Moon className="h-5 w-5 shrink-0" /> : <Sun className="h-5 w-5 shrink-0" />}
              {isDark ? menuLabels.darkModeActive : menuLabels.lightModeActive}
            </span>
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
              className="shrink-0 native-user-actions-theme-switch"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              window.setTimeout(() => setLangSheetOpen(true), 50);
            }}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left text-base hover:bg-muted active:bg-muted"
          >
            <Globe className="h-5 w-5 shrink-0" />
            {langNames[language]}
          </button>
          <div className="my-2 border-t border-border" />
          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left text-base font-medium text-red-600 dark:text-red-400 hover:bg-muted active:bg-muted"
          >
            {isLoggingOut ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-red-400" />
            ) : (
              <LogOut className="h-5 w-5 shrink-0" />
            )}
            {menuLabels.logout}
          </button>
        </div>
      </NativeOverlaySheet>
      <NativeOptionSheet
        open={langSheetOpen}
        onOpenChange={setLangSheetOpen}
        title={langNames[language]}
        value={language}
        options={languageOptions}
        onSelect={(lang) => {
          setLanguage(lang as Language);
          setLangSheetOpen(false);
        }}
      />
    </>
  );

  const profileBtnClass = floating
    ? "native-floating-profile-btn text-foreground hover:bg-muted/80 bg-background/95 backdrop-blur-md border border-border shadow-md rounded-full"
    : isNativeApp
      ? "native-header-icon-btn native-header-profile-btn text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/15"
      : "text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/15 [&_svg]:size-5 sm:[&_svg]:size-4";

  return (
    <div className={cn("flex items-center", floating ? "" : "space-x-4")}>
      {user ? (
        <>
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Button
              variant="ghost"
              size="headerIcon"
              className={`text-xs p-2 h-auto relative ${profileBtnClass}`}
              onClick={handleClick}
            >
              <UserCircle className={floating ? "h-7 w-7" : undefined} />
              {hasUnreadItems && (
                <span
                  className={`absolute w-2.5 h-2.5 bg-red-500 rounded-full ${
                    isNativeApp
                      ? "native-header-unread-dot"
                      : "top-1.5 right-1.5"
                  }`}
                />
              )}
            </Button>
            {isNativeApp
              ? nativeUserMenu
              : isOpen && createPortal(<DropdownContent />, document.body)}
          </div>
          {!hideHomeButton && (
            <Button
              variant="ghost"
              size="headerIcon"
              className={`text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/15 text-xs p-2 h-auto ${
                isNativeApp
                  ? "native-header-icon-btn native-header-home-btn"
                  : "[&_svg]:size-5 sm:[&_svg]:size-4"
              }`}
              onClick={() => navigate("/dashboard")}
              aria-label={menuLabels.home ?? "Hem"}
            >
              <Home className={isNativeApp ? undefined : "h-5 w-5 sm:h-4 sm:w-4"} />
            </Button>
          )}
        </>
      ) : guestBottomNavVisible ? (
        <>
          <div className="flex items-center gap-1">
            {isDark ? (
              <Moon className="h-4 w-4 text-accent" />
            ) : (
              <Sun className="h-4 w-4 text-accent" />
            )}
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
              className="scale-90"
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1">
            {isDark ? (
              <Moon className="h-4 w-4 text-accent" />
            ) : (
              <Sun className="h-4 w-4 text-accent" />
            )}
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
              className="scale-90"
            />
          </div>
          <Button
            variant="ghost"
            size="headerIcon"
            className="text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/15 text-sm font-medium px-4"
            onClick={handleLogin}
          >
            {menuLabels.login}
          </Button>
        </>
      )}
    </div>
  );
};

export default UserActions;
